import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Film, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * MediaUploader — drag-drop or click to upload images / videos.
 * Props:
 *   images:   string[]              current image URLs
 *   videos:   string[]              current video URLs
 *   onChange: ({images, videos}) => void
 *   accept:   "image" | "video" | "both"  (default "both")
 */
export default function MediaUploader({ images = [], videos = [], onChange, onVideoOptimized, accept = "both" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  // Progress for the file currently transferring: { name, pct, loaded, total }
  const [progress, setProgress] = useState(null);

  const acceptAttr = accept === "image"
    ? "image/*"
    : accept === "video" ? "video/*" : "image/*,video/*";

  const upload = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/admin/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        // e.total may be undefined for chunked encoding — fall back to file.size
        const total = e.total || file.size || 0;
        const loaded = e.loaded || 0;
        const pct = total > 0 ? Math.min(99, Math.round((loaded / total) * 100)) : 0;
        setProgress({ name: file.name, pct, loaded, total });
      },
    });
    return data; // { kind, url, path, ... }
  };

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    const newImages = [...images];
    const newVideos = [...videos];
    let okCount = 0;
    for (const f of fileList) {
      try {
        const res = await upload(f);
        const fullUrl = absoluteUrl(res.url);
        if (res.kind === "video") {
          newVideos.push(fullUrl);
          // If the backend already synchronously returned variants (short clip
          // or webm source), forward them right away…
          if (onVideoOptimized && (res.webm_url || res.poster_url)) {
            onVideoOptimized({
              mp4_url: fullUrl,
              webm_url: res.webm_url ? absoluteUrl(res.webm_url) : "",
              poster_url: res.poster_url ? absoluteUrl(res.poster_url) : "",
            });
          } else if (res.base && res.variants_status === "processing") {
            // …otherwise ffmpeg is running in the background — poll for the
            // WebM + poster (up to ~3 min). This avoids Cloudflare's 100s
            // origin timeout on large videos.
            pollVideoVariants(res.base, fullUrl);
          }
        }
        else newImages.push(fullUrl);
        okCount += 1;
      } catch (e) {
        toast.error(`${f.name}: ${formatErr(e)}`);
      }
    }
    setBusy(false);
    setProgress(null);
    if (okCount) {
      onChange({ images: newImages, videos: newVideos });
      toast.success(`${okCount} file${okCount > 1 ? "s" : ""} uploaded`);
    }
  };

  const pollVideoVariants = async (base, mp4Url) => {
    // Non-blocking: fires off polling and returns. Every 4s for up to 45 tries (3 min).
    // Silently gives up if ffmpeg doesn't finish — the raw MP4 still plays.
    const MAX_TRIES = 45;
    const INTERVAL_MS = 4000;
    let notified = false;
    const toastId = toast.loading("Optimising video (WebM + poster)…", { duration: Infinity });
    for (let i = 0; i < MAX_TRIES; i++) {
      await new Promise((r) => setTimeout(r, INTERVAL_MS));
      try {
        const { data } = await api.get(`/admin/upload/status/${base}`);
        if (data.status === "ready") {
          if (onVideoOptimized && !notified) {
            notified = true;
            onVideoOptimized({
              mp4_url: mp4Url,
              webm_url: data.webm_url ? absoluteUrl(data.webm_url) : "",
              poster_url: data.poster_url ? absoluteUrl(data.poster_url) : "",
            });
          }
          toast.success("Video optimised — WebM + poster ready", { id: toastId });
          return;
        }
        if (data.status === "failed") {
          toast.message("Optimisation skipped — the raw MP4 will play fine", { id: toastId });
          return;
        }
      } catch {
        // transient — keep polling
      }
    }
    toast.message("Optimisation still running — reload the page in a minute to pick up WebM + poster", { id: toastId, duration: 6000 });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    handleFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeImage = (i) => onChange({ images: images.filter((_, idx) => idx !== i), videos });
  const removeVideo = (i) => onChange({ images, videos: videos.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4" data-testid="media-uploader">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`w-full border border-dashed py-8 px-6 flex flex-col items-center justify-center gap-3 transition-colors cursor-pointer ${
          drag ? "border-[#C8A97E] bg-[rgba(200,169,126,0.05)]" : "border-[rgba(244,244,240,0.18)] hover:border-[#C8A97E]"
        }`}
        data-testid="upload-dropzone"
      >
        {busy ? (
          <div className="w-full max-w-xl flex flex-col items-center gap-3" data-testid="upload-progress">
            <Loader2 size={22} strokeWidth={1.5} className="text-gold animate-spin" />
            {progress ? (
              <>
                <div className="w-full h-1.5 bg-[rgba(244,244,240,0.08)] overflow-hidden">
                  <div
                    className="h-full bg-[#C8A97E] transition-all duration-200"
                    style={{ width: `${progress.pct}%` }}
                    data-testid="upload-progress-bar"
                  />
                </div>
                <div className="flex items-center justify-between w-full text-[10px] uppercase tracking-[0.18em] text-muted">
                  <span className="truncate max-w-[60%]" title={progress.name}>{progress.name}</span>
                  <span data-testid="upload-progress-pct">
                    {progress.pct}% · {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
                  </span>
                </div>
              </>
            ) : (
              <span className="text-xs uppercase tracking-[0.2em] text-muted">Uploading…</span>
            )}
          </div>
        ) : (
          <>
            <Upload size={22} strokeWidth={1.5} className="text-gold" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted text-center">
              Drag & drop {accept === "image" ? "images" : accept === "video" ? "videos" : "images or videos"} here, or click to browse
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted/70">
              JPG · PNG · WEBP · MP4 · WEBM · MOV
            </span>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptAttr}
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          data-testid="file-input"
        />
      </button>

      {images.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ImageIcon size={14} strokeWidth={1.5} className="text-gold" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted">Images ({images.length})</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {images.map((src, i) => (
              <div key={src} className="relative group">
                <img src={src} alt="" className="w-full aspect-[3/4] object-cover border border-[rgba(244,244,240,0.08)]" />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 p-1 bg-black/60 backdrop-blur-sm text-white hover:text-gold opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-image-${i}`}
                  aria-label="Remove image"
                >
                  <X size={12} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {videos.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Film size={14} strokeWidth={1.5} className="text-gold" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted">Videos ({videos.length})</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {videos.map((src, i) => (
              <div key={src} className="relative group">
                <video src={src} muted loop playsInline className="w-full aspect-video object-cover border border-[rgba(244,244,240,0.08)]" />
                <button
                  type="button"
                  onClick={() => removeVideo(i)}
                  className="absolute top-1 right-1 p-1 bg-black/60 backdrop-blur-sm text-white hover:text-gold opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-video-${i}`}
                  aria-label="Remove video"
                >
                  <X size={12} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
